import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, LockKeyhole, ShieldCheck, Users, TrendingUp, HeartHandshake, Scale, Sparkles, Package, HandHeart, Handshake, Gift, UsersRound, LifeBuoy } from "lucide-react";
import { PublicShell } from "../../components/Layouts";
import { api } from "../../lib/api";

// Rotating phrases typed into the hero like a terminal cursor. Each line is
// typed, then held, then erased, then the next line types in. Keeps rhythm
// deliberate — never faster than a human can comfortably read.
const TYPE_LINES = [
  "grow together.",
  "lift each other up.",
  "build real momentum.",
  "turn help into trust.",
];

function useTypewriter(lines, { typeMs = 70, holdMs = 1600, eraseMs = 35 } = {}) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(true);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    const full = lines[idx % lines.length];
    if (typing) {
      if (text.length < full.length) {
        timer.current = setTimeout(() => setText(full.slice(0, text.length + 1)), typeMs);
      } else {
        timer.current = setTimeout(() => setTyping(false), holdMs);
      }
    } else {
      if (text.length > 0) {
        timer.current = setTimeout(() => setText(text.slice(0, -1)), eraseMs);
      } else {
        setTyping(true);
        setIdx((i) => (i + 1) % lines.length);
      }
    }
    return () => clearTimeout(timer.current);
  }, [text, typing, idx, lines, typeMs, holdMs, eraseMs]);
  return text;
}

const HELP_ICONS = [
  { Icon: HandHeart, label: "Give" },
  { Icon: Handshake, label: "Connect" },
  { Icon: HeartHandshake, label: "Support" },
  { Icon: Gift, label: "Share" },
  { Icon: UsersRound, label: "Grow" },
  { Icon: LifeBuoy, label: "Rescue" },
];

export default function Home() {
  const [mode, setMode] = useState("SETUP");
  const [rates, setRates] = useState([10, 6, 4, 1]);
  const [packages, setPackages] = useState([]);
  const typed = useTypewriter(TYPE_LINES);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      setMode(r.data?.mode || "SETUP");
      if (r.data?.referral_rates) setRates(r.data.referral_rates);
    }).catch(() => {});
    api.get("/packages").then((r) => setPackages((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const readyLabel = mode === "LIVE" ? "LIVE" : "READY";
  const readyCopy = mode === "LIVE" ? "On-chain verification" : "Launching soon";

  return (
    <PublicShell>
      <main>
        <section className="hero">
          <div className="hero-copy reveal">
            <div className="eyebrow"><span className="pulse" /> A STRUCTURED HELPING PLATFORM</div>
            <h1>Help, be helped,<br /><em className="type-line" data-testid="hero-typewriter">{typed}<span className="type-caret" /></em></h1>
            <p>World Help Agency is a transparent community where structured participation turns everyday help into measurable momentum — for you, your family, and your network.</p>
            <div className="hero-actions">
              <Link data-testid="hero-get-started-button" to="/register" className="btn btn-accent">Join the community <ArrowRight size={17} /></Link>
              <Link data-testid="hero-packages-button" to="/packages" className="btn btn-ghost"><Package size={16} /> Packages</Link>
            </div>
            <div className="trust-line"><ShieldCheck size={15} /> On-chain verified · 4-level unilevel referral · No private key custody</div>
          </div>
          <div className="hero-visual">
            <div className="network-card">
              <div className="network-top"><span>COMMUNITY PULSE</span><span className="green-dot">● Active</span></div>
              <div className="network-orbit">
                <div className="orbit orbit-one" />
                <div className="orbit orbit-two" />
                <div className="core"><HeartHandshake size={30} /><small>HELPING</small></div>
                <i className="node n1" /><i className="node n2" /><i className="node n3" />
              </div>
              <div className="network-bottom"><span>Participation index</span><strong>+18.42% <TrendingUp size={15} /></strong></div>
            </div>
          </div>
        </section>

        <section className="helping-strip" aria-label="Helping rituals" data-testid="helping-strip">
          {HELP_ICONS.map(({ Icon, label }, i) => (
            <div className="helping-icon" key={label} style={{ animationDelay: `${i * 0.35}s` }} data-testid={`helping-icon-${i}`}>
              <span className="helping-ring" />
              <span className="helping-core"><Icon size={22} /></span>
              <small>{label}</small>
            </div>
          ))}
        </section>

        <section className="metric-strip" data-testid="hero-metrics">
          <div><strong>04</strong><span>Referral levels</span></div>
          <div><strong>24/7</strong><span>Transparent dashboard</span></div>
          <div><strong>BEP20</strong><span>USDT on BNB Chain</span></div>
          <div><strong data-testid="mode-readiness">{readyLabel}</strong><span data-testid="mode-copy">{readyCopy}</span></div>
        </section>

        {packages.length > 0 && (
          <section className="section home-packages" id="packages">
            <div className="section-heading">
              <div>
                <div className="eyebrow">LIVE PACKAGES</div>
                <h2>Pick a tier,<br /><em>get your unique deposit amount.</em></h2>
              </div>
              <p>Days, daily production and end-of-term earnings are published live from the platform. Tap any package to open its deposit screen — we'll reserve a unique cent-level amount so your transfer is auto-matched.</p>
            </div>
            <div className="home-packages-grid">
              {packages.map((p, i) => (
                <Link key={p.id} to={`/packages`} className="home-pkg-card" data-testid={`home-pkg-${i}`} style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="pkg-head">
                    <span className="pkg-eyebrow">0{i + 1}</span>
                    <strong>{p.name}</strong>
                  </div>
                  <div className="pkg-price">${Number(p.price).toLocaleString()}</div>
                  <ul className="pkg-stats">
                    <li><span>Duration</span><b>{p.days} days</b></li>
                    <li><span>Daily</span><b>${p.daily_production}</b></li>
                    <li><span>End of term</span><b>${p.end_of_term}</b></li>
                  </ul>
                  <span className="btn btn-dark pkg-deposit-btn">Deposit <ArrowRight size={14} /></span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="section" id="how">
          <div className="section-heading">
            <div>
              <div className="eyebrow">THE WORLD HELP AGENCY METHOD</div>
              <h2>Everyone wins<br /><em>when everyone shows up.</em></h2>
            </div>
            <p>Four-level commissions reward the people who introduce the people who introduce you — rewarding the whole chain of trust.</p>
          </div>
          <div className="imagery-band">
            <figure data-testid="story-image-1" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=900&q=70)" }}><figcaption>Members supporting members</figcaption></figure>
            <figure data-testid="story-image-2" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=70)" }}><figcaption>A community that keeps showing up</figcaption></figure>
            <figure data-testid="story-image-3" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=900&q=70)" }}><figcaption>Trust that compounds across levels</figcaption></figure>
          </div>
          <div className="feature-grid">
            <article className="feature">
              <div className="feature-icon"><HeartHandshake /></div>
              <h3>Rooted in helping</h3>
              <p>Every participation tier is a structured way to help the network move forward — and have your part of it tracked, visible, and rewarded.</p>
              <ArrowRight size={17} />
            </article>
            <article className="feature">
              <div className="feature-icon"><Scale /></div>
              <h3>Transparent by design</h3>
              <p>Package pricing, daily production, referral rates and the deposit wallet are configurable — and shown to you in plain English, not fine print.</p>
              <ArrowRight size={17} />
            </article>
            <article className="feature">
              <div className="feature-icon"><LockKeyhole /></div>
              <h3>Safe custody model</h3>
              <p>We never ask for or store your private key. Deposits are verified on-chain; withdrawals are reviewed and released by the operations team.</p>
              <ArrowRight size={17} />
            </article>
          </div>
        </section>

        <section className="section referral-band" style={{ paddingTop: 20 }}>
          <div className="section-heading">
            <div>
              <div className="eyebrow">COMMISSIONS</div>
              <h2>Four levels,<br /><em>published rates.</em></h2>
            </div>
            <p>Rates below are live from the platform. Historical commissions are snapshotted on activation — your past earnings never change if rates are updated later.</p>
          </div>
          <div className="rates-grid">
            {[1, 2, 3, 4].map((lvl, idx) => (
              <div className="rate-card" key={lvl} data-testid={`rate-card-${lvl}`}>
                <span>LEVEL {lvl}</span>
                <strong>{rates[idx] ?? "—"}%</strong>
                <small>{["Direct referral", "2nd generation", "3rd generation", "4th generation"][idx]}</small>
              </div>
            ))}
          </div>
        </section>

        <section className="cta-band">
          <div>
            <div className="eyebrow"><Sparkles size={13} style={{ marginRight: 6 }} /> READY WHEN YOU ARE</div>
            <h2>One account.<br /><em>One structured path forward.</em></h2>
            <p>Create your World Help Agency account in under a minute and activate your first participation when you are ready.</p>
          </div>
          <Link to="/register" className="btn btn-accent" data-testid="cta-join"><Users size={16} /> Create free account <ArrowRight size={16} /></Link>
        </section>
      </main>
    </PublicShell>
  );
}
